import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

export function useToastContext() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const remove = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const add = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2);
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) {
      timers.current[id] = setTimeout(() => remove(id), duration);
    }
    return id;
  }, [remove]);

  return (
    <ToastContext.Provider value={{ add, remove }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={remove} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onRemove }) {
  if (toasts.length === 0) return null;
  return (
    <>
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(110%); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .toast-item { animation: toastIn 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .toast-close:hover { opacity: 1 !important; }
      `}</style>
      <div style={tc.container}>
        {toasts.map(t => <Toast key={t.id} toast={t} onRemove={onRemove} />)}
      </div>
    </>
  );
}

const ICONS  = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };
const COLORS = {
  success: { bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.25)', icon: '#10b981', bar: '#10b981' },
  error:   { bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.25)',  icon: '#ef4444', bar: '#ef4444' },
  warning: { bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.25)', icon: '#f59e0b', bar: '#f59e0b' },
  info:    { bg: 'rgba(99,102,241,0.12)',  border: 'rgba(99,102,241,0.25)', icon: '#818cf8', bar: '#6366f1' },
};

function Toast({ toast, onRemove }) {
  const c = COLORS[toast.type] || COLORS.info;
  return (
    <div className="toast-item" style={{ ...tc.toast, background: c.bg, border: `1px solid ${c.border}` }}>
      <div style={{ ...tc.bar, background: c.bar }} />
      <span style={{ ...tc.icon, color: c.icon }}>{ICONS[toast.type]}</span>
      <span style={tc.message}>{toast.message}</span>
      <button className="toast-close" style={tc.close} onClick={() => onRemove(toast.id)}>✕</button>
    </div>
  );
}

const tc = {
  container: {
    position: 'fixed', top: 24, right: 24, zIndex: 9999,
    display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none',
  },
  toast: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '12px 14px 12px 10px', borderRadius: 10,
    minWidth: 280, maxWidth: 400,
    backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    pointerEvents: 'all', position: 'relative', overflow: 'hidden',
  },
  bar:     { position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, borderRadius: '10px 0 0 10px' },
  icon:    { fontSize: 13, fontWeight: 700, flexShrink: 0, width: 18, textAlign: 'center' },
  message: { flex: 1, fontSize: 13, color: 'var(--toast-text, #1e293b)', lineHeight: 1.4, fontWeight: 500 },
  close:   { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11, padding: '2px 4px', flexShrink: 0, opacity: 0.6, transition: 'opacity 0.15s' },
};
